import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Heart, Plus, Sparkles } from 'lucide-react';

/**
 * Banner encouraging users to submit service providers
 * Use on Services, Adoption, and similar directory pages
 */
const CommunitySubmitBanner = ({ 
  variant = 'default', // 'default', 'compact', 'floating'
  serviceType = 'service' // 'service', 'vet', 'adoption', 'groomer'
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const messages = {
    service: {
      title: "Know a great pet service?",
      subtitle: "Help grow our community! Submit a vet, groomer, trainer, or any pet service you love.",
      cta: "Submit a Service"
    },
    vet: {
      title: "Know a great vet?",
      subtitle: "Share your favorite veterinarian with the community and help other pet owners.",
      cta: "Submit a Vet"
    },
    adoption: {
      title: "Are you a rescue, shelter, or other non-profit?",
      subtitle: "Get a free membership with your own feed page! Help pets find homes by listing your adoptable animals.",
      cta: "Apply Now"
    },
    groomer: {
      title: "Found an amazing groomer?",
      subtitle: "Share your discovery with other pet owners in our community.",
      cta: "Submit a Groomer"
    }
  };

  const msg = messages[serviceType] || messages.service;

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Heart className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{msg.title}</p>
            <p className="text-sm text-gray-600">{msg.subtitle}</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/submit-service')}
          className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
          data-testid="community-submit-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          {msg.cta}
        </Button>
      </div>
    );
  }

  if (variant === 'floating') {
    return (
      <div className="fixed bottom-6 right-6 z-40">
        <Button
          onClick={() => navigate('/submit-service')}
          className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg rounded-full px-6 py-3 h-auto"
          data-testid="community-submit-floating-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {msg.cta}
        </Button>
      </div>
    );
  }

  // Default variant - full banner
  return (
    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 rounded-2xl p-6 md:p-8 text-white shadow-lg overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
            <Sparkles className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-1">{msg.title}</h3>
            <p className="text-white/90 text-lg max-w-md">
              {msg.subtitle}
            </p>
          </div>
        </div>
        
        <Button
          onClick={() => navigate('/submit-service')}
          size="lg"
          className="bg-white text-amber-600 hover:bg-white/90 font-semibold shadow-md shrink-0"
          data-testid="community-submit-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {msg.cta}
        </Button>
      </div>
      
      <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-2 text-sm text-white/80">
        <Heart className="w-4 h-4" />
        <span>Community-powered • Submissions reviewed by club admins</span>
      </div>
    </div>
  );
};

export default CommunitySubmitBanner;
